import { generateError, InputWrapper } from './Input'
import { forwardRef, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import { useBusinessContext } from '@root/context'
import { fetchPeople } from '@root/query'
import { getUserDisplayName } from '@root/utils'
import { MentionsInput, Mention } from 'react-mentions'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const LINE_HEIGHT = 1.5
const SUGGESTIONS_Z_INDEX = 10000
const suggestionsPortalHost = typeof document !== 'undefined' ? document.body : undefined

const rowsToHeight = (rows: number) => `${(rows * LINE_HEIGHT).toFixed(2)}em`

interface Props {
  error: any
  onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  onChangeController: (value: string) => void
  actionElement: React.ReactNode
  name: string
  value: string
  /** Rows always visible. Without it the field starts one row tall. */
  minRows?: number
  /** Rows shown before the field scrolls. Without it the field keeps growing. */
  maxRows?: number
}

const InputSend = forwardRef<any, Props>(
  ({ error, onBlur, onChangeController, actionElement, name, value, minRows, maxRows }, ref) => {
    const [isFocused, setFocused] = useState(false)
    const _error = generateError(error)
    const [t] = useTranslation()

    const { slug } = useBusinessContext()

    const { data } = useQuery({
      queryKey: [slug, 'people'],
      queryFn: () => fetchPeople({ slug }, { page_size: 5000 })
    })

    const users =
      data?.results.map(usership => {
        return {
          display: getUserDisplayName(usership),
          id: usership.user_id,
          email: usership.email
        }
      }) || []

    /**
     * react-mentions sizes the field from the highlighter and overlays the textarea on top of it,
     * so both need the same height constraints to stay aligned.
     */
    const heightStyle = useMemo(() => {
      const constraints = {
        ...(minRows ? { minHeight: rowsToHeight(minRows) } : {}),
        ...(maxRows ? { maxHeight: rowsToHeight(maxRows) } : {})
      }

      return {
        highlighter: constraints,
        input: { ...constraints, ...(maxRows ? { overflow: 'auto' } : {}) },
        suggestions: {
          zIndex: SUGGESTIONS_Z_INDEX
        }
      }
    }, [maxRows, minRows])

    return (
      <>
        <InputWrapper
          label=""
          inputId={`input-send-${name}`}
          actionComponent={actionElement}
          errorMsg={_error}
          isFocused={isFocused}
        >
          <MentionsInputStyled
            id={`input-send-${name}`}
            placeholder={t('components.inputSend.placeholder')}
            name={name}
            style={heightStyle}
            ref={el => {
              if (typeof ref === 'function') {
                ref(el)
              } else if (ref) {
                ref.current = el
              }
            }}
            suggestionsPortalHost={suggestionsPortalHost}
            value={value}
            onChange={e => {
              onChangeController?.(e.target.value)
            }}
            onFocus={() => {
              setFocused(true)
            }}
            onBlur={e => {
              setFocused(false)
              onBlur?.(e)
            }}
            allowSuggestionsAboveCursor={true}
            customSuggestionsContainer={children => <StyledContainer>{children}</StyledContainer>}
            allowSpaceInQuery={true}
          >
            <Mention
              trigger="@"
              markup="@[__display__](__id__)"
              data={(search: string) => {
                const searchLower = search.toLowerCase()
                return users.filter(user => {
                  return (
                    user.display.toLowerCase().includes(searchLower) ||
                    user.email.toLowerCase().includes(searchLower)
                  )
                })
              }}
              displayTransform={(_, display) => `@${display}`}
              renderSuggestion={(suggestion, _search, _highlightedDisplay, _index, focused) => {
                return (
                  <StyledSelection className={focused && 'keyboard-focused'}>
                    {suggestion.display}
                    <StyledEmail>{suggestion.email}</StyledEmail>
                  </StyledSelection>
                )
              }}
              appendSpaceOnAdd={true}
            />
          </MentionsInputStyled>
        </InputWrapper>
      </>
    )
  }
)

export default InputSend

const MentionsInputStyled = styled(MentionsInput)`
  flex: 1;
  border: none;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: ${LINE_HEIGHT};

  & > * > textarea {
    border: none;
    line-height: inherit;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.borderStrong} transparent;
  }

  & > * > textarea:focus {
    outline: none;
  }

  & > * > textarea::-webkit-scrollbar {
    width: 8px;
  }

  & > * > textarea::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.borderStrong};
    border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  & > * > textarea::-webkit-scrollbar-track {
    background: transparent;
  }
`

const StyledContainer = styled(motion.div).attrs({
  initial: { y: 10, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: 0, opacity: 0 }
})`
  display: flex;
  max-height: 200px;
  max-width: 250px;
  overflow: auto;
  flex-direction: column;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledSelection = styled.div`
  padding: ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  width: max-content;

  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`
const StyledEmail = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`
